import React, { useCallback, useEffect, useRef, useState } from 'react';
import PageWrapper from '../components/PageWrapper';
import { useSiteContext } from '../SiteContext';
import { Page } from '../interfaces/PagesInterface';
import config from '../configLoader';
import ProjectsAPI from '../api/projects';
import {
  getProjectPWRates,
  postProjectPWRate,
  putProjectPWRate,
  putAdminProject,
  getPWFringeTypes,
  getErrorMessage,
} from '../api/prevailingWage';
import { Project } from '../interfaces/Project';
import {
  PWClassificationRateGroup,
  PWClassificationRateRecord,
  PWFringeType,
} from '../interfaces/PrevailingWage';
import {
  AlertTriangle,
  Briefcase,
  CheckCircle,
  ChevronRight,
  DollarSign,
  Edit,
  Info,
  ArrowLeftRight,
  Lock,
  Plus,
  Search,
  X,
} from 'lucide-react';
import './PWPagesV2.css';

/* ── types ──────────────────────────────────────────────────────────────── */
type FringeRow = { pwFringeTypeID: number; hourlyAmount: string };
type Toast = { id: number; type: 'ok' | 'err'; message: string };
type LiteProject = {
  projectID: number;
  altProjectID: string;
  name: string;
  isPrevailingWage?: boolean;
  active: boolean;
};

/* ── helpers ──────────────────────────────────────────────────────────────── */
function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(s: string | undefined | null): string {
  if (!s) return '—';
  const iso = s.slice(0, 10);
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return s;
  return `${Number(m)}/${Number(d)}/${y}`;
}

/* ── recents localStorage ─────────────────────────────────────────────────── */
const RECENTS_KEY = 'pw.recentProjects';
const MAX_RECENTS = 6;

function loadRecents(): LiteProject[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (r: unknown): r is LiteProject =>
        typeof r === 'object' && r !== null &&
        typeof (r as LiteProject).projectID === 'number'
    );
  } catch {
    return [];
  }
}

function saveRecent(proj: LiteProject): void {
  const existing = loadRecents().filter(r => r.projectID !== proj.projectID);
  const next: LiteProject[] = [proj, ...existing].slice(0, MAX_RECENTS);
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch { /* quota — ignore */ }
}

/* ── rate status ─────────────────────────────────────────────────────────── */
function getRateStatus(
  rate: PWClassificationRateRecord,
  todayStr: string,
  sortedRates: PWClassificationRateRecord[],
): 'current' | 'scheduled' | 'past' {
  const past = sortedRates.filter(r => r.effectiveDate <= todayStr);
  const currentId = past.length ? past[0].pwClassificationRateID : null;
  if (rate.pwClassificationRateID === currentId) return 'current';
  if (rate.effectiveDate > todayStr) return 'scheduled';
  return 'past';
}

/* ── component ───────────────────────────────────────────────────────────── */
const PWProjectSetupPage: React.FC = () => {
  const { setLoading, createPopup, userRole, setPage, userID } = useSiteContext();
  const isReadOnly = config.readOnlyPrevailingWagePages;
  const TODAY = getTodayStr();

  /* access gate */
  useEffect(() => {
    if (!userRole.admin) {
      createPopup('Access Denied', 'You do not have permission to access this page.');
      setPage(Page.Dashboard);
      return;
    }
    if (!config.showPrevailingWagePages) {
      createPopup('Access Denied', 'This feature is not available.');
      setPage(Page.Dashboard);
    }
  }, [userRole, setPage, createPopup]);

  /* ── picker state ─────────────────────────────────────────────────────── */
  const [query, setQuery] = useState('');
  const [queryResults, setQueryResults] = useState<Project[]>([]);
  const [queryLoading, setQueryLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [hiIdx, setHiIdx] = useState(-1);
  const [recentProjects, setRecentProjects] = useState<LiteProject[]>(loadRecents);
  const queryRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pickerInputWrapRef = useRef<HTMLDivElement>(null);
  /** Fixed-position coords for the floating results menu — keeps it out of
      every scroll container (same pattern as the Projects page picker). */
  const [menuPos, setMenuPos] = useState<{ left: number; top: number; width: number }>({ left: 0, top: 0, width: 0 });

  const placeMenu = useCallback(() => {
    const el = pickerInputWrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({ left: r.left, top: r.bottom + 5, width: r.width });
  }, []);

  /* keep the fixed-position menu anchored while open */
  useEffect(() => {
    if (!showMenu) return;
    placeMenu();
    window.addEventListener('resize', placeMenu);
    document.addEventListener('scroll', placeMenu, true);
    return () => {
      window.removeEventListener('resize', placeMenu);
      document.removeEventListener('scroll', placeMenu, true);
    };
  }, [showMenu, placeMenu]);

  /* ── selected project ─────────────────────────────────────────────────── */
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isPrevailingWage, setIsPrevailingWage] = useState(false);
  const [groups, setGroups] = useState<PWClassificationRateGroup[]>([]);
  const [openRows, setOpenRows] = useState<Set<number>>(new Set());

  /* ── PW flag confirm ──────────────────────────────────────────────────── */
  const [confirmPwToggle, setConfirmPwToggle] = useState<{ target: boolean } | null>(null);
  const [pwToggling, setPwToggling] = useState(false);

  /* ── rate modal ───────────────────────────────────────────────────────── */
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<PWClassificationRateRecord | null>(null);
  const [rateClassificationId, setRateClassificationId] = useState<number | null>(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [baseHourlyRate, setBaseHourlyRate] = useState('');
  const [notes, setNotes] = useState('');
  const [fringeRows, setFringeRows] = useState<FringeRow[]>([]);
  const [fringeTypeOptions, setFringeTypeOptions] = useState<PWFringeType[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [valErrs, setValErrs] = useState<Record<string, string>>({});

  /* ── toasts ───────────────────────────────────────────────────────────── */
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  function addToast(type: 'ok' | 'err', message: string) {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4200);
  }

  /* ── typeahead ────────────────────────────────────────────────────────── */
  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) { setQueryResults([]); setShowMenu(false); return; }
    setQueryLoading(true);
    setShowMenu(true);
    try {
      const results = await ProjectsAPI.typeahead(q, 25);
      setQueryResults(results);
    } catch (e) {
      setQueryResults([]);
      console.error(e);
    } finally {
      setQueryLoading(false);
    }
  }, []);

  const handleQueryChange = (val: string) => {
    setQuery(val);
    setHiIdx(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) { setQueryResults([]); setShowMenu(false); return; }
    debounceRef.current = setTimeout(() => doSearch(val), 220);
  };

  /* close picker menu on outside click */
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  /* ── load rates ───────────────────────────────────────────────────────── */
  const loadRates = async (projectID: number) => {
    setLoading(true);
    try {
      const data = await getProjectPWRates(projectID);
      setGroups(data);
    } catch (e) {
      addToast('err', getErrorMessage(e));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  /* ── select project ───────────────────────────────────────────────────── */
  const selectProject = async (proj: Project) => {
    setQuery('');
    setQueryResults([]);
    setShowMenu(false);
    setHiIdx(-1);
    setOpenRows(new Set());
    setSelectedProject(proj);
    setIsPrevailingWage(!!proj.isPrevailingWage);
    const lite: LiteProject = {
      projectID: proj.projectID,
      altProjectID: proj.altProjectID,
      name: proj.name,
      isPrevailingWage: proj.isPrevailingWage,
      active: proj.active,
    };
    saveRecent(lite);
    setRecentProjects(loadRecents());
    await loadRates(proj.projectID);
  };

  const handlePickerKeyDown = (e: React.KeyboardEvent) => {
    if (!showMenu || queryResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHiIdx(i => Math.min(i + 1, queryResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHiIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && hiIdx >= 0) {
      e.preventDefault();
      selectProject(queryResults[hiIdx]);
    } else if (e.key === 'Escape') {
      setShowMenu(false);
    }
  };

  /* ── change project (back to welcome) ────────────────────────────────── */
  const changeProject = () => {
    setSelectedProject(null);
    setGroups([]);
    setOpenRows(new Set());
    setTimeout(() => queryRef.current?.focus(), 50);
  };

  /* ── PW flag ──────────────────────────────────────────────────────────── */
  const requestPwToggle = (checked: boolean) => {
    if (isReadOnly || !selectedProject) return;
    setConfirmPwToggle({ target: checked });
  };

  const confirmTogglePw = async () => {
    if (!selectedProject || !confirmPwToggle) return;
    setPwToggling(true);
    try {
      const res = await putAdminProject(selectedProject.projectID, {
        isPrevailingWage: confirmPwToggle.target,
      });
      const newVal = !!res.isPrevailingWage;
      setIsPrevailingWage(newVal);
      setSelectedProject(prev => (prev ? { ...prev, isPrevailingWage: newVal } : prev));
      saveRecent({
        projectID: selectedProject.projectID,
        altProjectID: selectedProject.altProjectID,
        name: selectedProject.name,
        isPrevailingWage: newVal,
        active: selectedProject.active,
      });
      setRecentProjects(loadRecents());
      addToast('ok', `Project ${newVal ? 'marked as' : 'removed from'} prevailing wage.`);
    } catch (err) {
      addToast('err', getErrorMessage(err));
    } finally {
      setPwToggling(false);
      setConfirmPwToggle(null);
    }
  };

  /* ── toggle row expansion ─────────────────────────────────────────────── */
  const toggleRow = (id: number) => {
    setOpenRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /* ── rate modal helpers ───────────────────────────────────────────────── */
  const getGroupForClassification = (id: number | null) =>
    id == null ? null : groups.find(g => g.pwClassificationID === id) ?? null;

  const openAddRate = async (pwClassificationID?: number) => {
    if (isReadOnly) return;
    setEditingRate(null);
    setRateClassificationId(pwClassificationID ?? null);
    setEffectiveDate('');
    setBaseHourlyRate('');
    setNotes('');
    setFringeRows([]);
    setValErrs({});
    setLoading(true);
    try {
      const opts = await getPWFringeTypes(true);
      setFringeTypeOptions(opts);
      setRateModalOpen(true);
    } catch (err) {
      addToast('err', getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const openEditRate = async (rate: PWClassificationRateRecord) => {
    if (isReadOnly) return;
    setEditingRate(rate);
    setRateClassificationId(rate.pwClassificationID);
    setEffectiveDate(rate.effectiveDate.slice(0, 10));
    setBaseHourlyRate(String(rate.baseHourlyRate));
    setNotes(rate.notes ?? '');
    setFringeRows(
      rate.fringeItems.map(fi => ({
        pwFringeTypeID: fi.pwFringeTypeID,
        hourlyAmount: String(fi.hourlyAmount),
      }))
    );
    setValErrs({});
    setLoading(true);
    try {
      const opts = await getPWFringeTypes(true);
      setFringeTypeOptions(opts);
      setRateModalOpen(true);
    } catch (err) {
      addToast('err', getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const closeRateModal = () => {
    setRateModalOpen(false);
    setEditingRate(null);
    setRateClassificationId(null);
    setValErrs({});
  };

  const addFringeRow = () => {
    setFringeRows(prev => [...prev, { pwFringeTypeID: 0, hourlyAmount: '' }]);
  };

  const updateFringeRow = (idx: number, patch: Partial<FringeRow>) => {
    setFringeRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
    if (patch.pwFringeTypeID !== undefined) {
      setValErrs(prev => {
        const n = { ...prev };
        delete n[`fringe_dup_${idx}`];
        delete n[`fringe_type_${idx}`];
        return n;
      });
    }
    if (patch.hourlyAmount !== undefined) {
      setValErrs(prev => {
        const n = { ...prev };
        delete n[`fringe_amt_${idx}`];
        return n;
      });
    }
  };

  const removeFringeRow = (idx: number) => {
    setFringeRows(prev => prev.filter((_, i) => i !== idx));
    setValErrs(prev => {
      const n = { ...prev };
      delete n[`fringe_dup_${idx}`];
      delete n[`fringe_type_${idx}`];
      delete n[`fringe_amt_${idx}`];
      return n;
    });
  };

  /* live footer */
  const liveFringeTotal = fringeRows.reduce((sum, row) => {
    const v = parseFloat(row.hourlyAmount);
    return sum + (Number.isNaN(v) || v <= 0 ? 0 : v);
  }, 0);
  const liveBase = parseFloat(baseHourlyRate);
  const livePkgTotal = (Number.isNaN(liveBase) ? 0 : liveBase) + liveFringeTotal;

  /* ── submit rate ──────────────────────────────────────────────────────── */
  const submitRate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly || !selectedProject) return;

    const errs: Record<string, string> = {};
    if (!editingRate && rateClassificationId == null) {
      errs.classification = 'PW Classification is required.';
    }
    if (!effectiveDate.trim()) errs.date = 'Effective date is required.';
    const base = parseFloat(baseHourlyRate);
    if (Number.isNaN(base) || base <= 0) errs.base = 'Must be a positive number.';

    const ids = new Set<number>();
    for (let i = 0; i < fringeRows.length; i++) {
      const row = fringeRows[i];
      if (!row.pwFringeTypeID) {
        errs[`fringe_type_${i}`] = 'Select a fringe type.';
      } else if (ids.has(row.pwFringeTypeID)) {
        errs[`fringe_dup_${i}`] = 'Duplicate fringe type.';
      } else {
        ids.add(row.pwFringeTypeID);
      }
      const amt = parseFloat(row.hourlyAmount);
      if (Number.isNaN(amt) || amt <= 0) {
        errs[`fringe_amt_${i}`] = 'Must be a positive number.';
      }
    }

    if (Object.keys(errs).length) { setValErrs(errs); return; }
    setValErrs({});

    const fringeItems = fringeRows.map(row => ({
      pwFringeTypeID: row.pwFringeTypeID,
      hourlyAmount: parseFloat(row.hourlyAmount),
    }));

    setSubmitting(true);
    try {
      if (editingRate) {
        await putProjectPWRate(selectedProject.projectID, editingRate.pwClassificationRateID, {
          effectiveDate: effectiveDate.trim(),
          baseHourlyRate: base,
          notes: notes.trim() || null,
          adminEmployeeID: userID || undefined,
          fringeItems: fringeItems.length ? fringeItems : undefined,
        });
        addToast('ok', 'Rate record updated.');
      } else {
        await postProjectPWRate(selectedProject.projectID, {
          pwClassificationID: rateClassificationId!,
          effectiveDate: effectiveDate.trim(),
          baseHourlyRate: base,
          notes: notes.trim() || null,
          adminEmployeeID: userID || undefined,
          fringeItems: fringeItems.length ? fringeItems : undefined,
        });
        addToast('ok', 'Rate record added.');
      }
      closeRateModal();
      await loadRates(selectedProject.projectID);
    } catch (err) {
      addToast('err', getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  /* ── derived ──────────────────────────────────────────────────────────── */
  const isWelcome = !selectedProject;
  const groupsWithRates = groups.filter(g => g.rates.length > 0);

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <PageWrapper title="Project PW Setup" backgroundColor="#f4f5f7">
      <div className="pwv2">
        <div className="pw2-page">

          {/* Breadcrumb */}
          <nav className="pw2-crumbs" aria-label="breadcrumb">
            <span>Prevailing Wage</span>
            <ChevronRight />
            <span className="here">Project PW Setup</span>
          </nav>

          {/* App header */}
          <div className="pw2-app-head">
            <div className="pw2-logo"><Briefcase /></div>
            <div>
              <h1 className="pw2-app-title">Project PW Setup</h1>
              <p className="pw2-app-sub">
                Configure effective-dated prevailing wage rate schedules (base rate + fringe package) per project and classification.
              </p>
            </div>
            {!isWelcome && (
              <div className="pw2-head-action">
                <button type="button" className="pw2-btn" onClick={changeProject}>
                  <ArrowLeftRight size={16} />
                  Change project
                </button>
              </div>
            )}
          </div>

          {/* Read-only note */}
          {isReadOnly && (
            <div className="pw2-ro-note">
              <Info />
              <span>
                <b>Read-only on this environment.</b>{' '}
                Rate schedules are managed on the production environment.
              </span>
            </div>
          )}

          {/* ── Welcome / picker ── */}
          {isWelcome ? (
            <div className="pw2-welcome-wrap">
              <div className="pw2-welcome-card">
                <div className="pw2-welcome">
                  <div className="pw2-w-ic"><Briefcase /></div>
                  <h2>Select a project</h2>
                  <p>
                    Search for a project to view and manage its prevailing wage rate schedule.
                  </p>

                  {/* Typeahead picker */}
                  <div ref={menuRef} className="pw2-picker" style={{ width: '100%', maxWidth: 'none' }}>
                    <span className="pp-label">Project</span>
                    <div className="pw2-picker-input" ref={pickerInputWrapRef}>
                      <Search />
                      <input
                        ref={queryRef}
                        placeholder="Search by project name or ID…"
                        value={query}
                        onChange={e => handleQueryChange(e.target.value)}
                        onKeyDown={handlePickerKeyDown}
                        onFocus={() => {
                          if (query.trim() && queryResults.length) setShowMenu(true);
                        }}
                        autoComplete="off"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                    </div>
                    {showMenu && (
                      <div
                        className="pw2-picker-menu"
                        style={{ left: menuPos.left, top: menuPos.top, width: menuPos.width }}
                      >
                        {queryLoading && (
                          <div className="pw2-picker-msg">Searching…</div>
                        )}
                        {!queryLoading && queryResults.length === 0 && (
                          <div className="pw2-picker-msg">No projects found.</div>
                        )}
                        {!queryLoading && queryResults.map((proj, i) => (
                          <div
                            key={proj.projectID}
                            className={`pw2-picker-opt${i === hiIdx ? ' hi' : ''}`}
                            onMouseDown={() => selectProject(proj)}
                            onMouseEnter={() => setHiIdx(i)}
                          >
                            <span className="po-code">{proj.altProjectID}</span>
                            <span className="po-name">{proj.name}</span>
                            {proj.isPrevailingWage && <span className="pw-tag">PW</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recents */}
                  {recentProjects.length > 0 && (
                    <div className="pw2-recent-line">
                      <div className="rl-h">Recent projects</div>
                      <div className="pw2-recent-chips">
                        {recentProjects.map(r => (
                          <button
                            key={r.projectID}
                            type="button"
                            className={`pw2-recent-chip${!r.active ? ' inactive' : ''}`}
                            onClick={() =>
                              selectProject({
                                projectID: r.projectID,
                                altProjectID: r.altProjectID,
                                name: r.name,
                                active: r.active,
                                hasDirectTasks: false,
                                isPrevailingWage: r.isPrevailingWage,
                              })
                            }
                          >
                            <span className="rc-code">{r.altProjectID}</span>
                            <span>{r.name}</span>
                            {r.isPrevailingWage && <span className="rc-pw">PW</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <p className="pw2-picker-hint">
                <Info />
                Includes active and inactive projects. Search by project name or ID.
              </p>
            </div>

          ) : (
            <>
              {/* ── Selected-project banner ── */}
              <div className="pw2-banner">
                <div className="ban-logo"><Briefcase /></div>
                <div className="ban-main">
                  <div className="ban-id">
                    <span className="code">{selectedProject.altProjectID}</span>
                    <span className={`pw2-pill ${selectedProject.active ? 'active' : 'inactive'}`}>
                      <span className="dot" />
                      {selectedProject.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <h1>{selectedProject.name}</h1>
                </div>
                <div className="ban-right">
                  {isReadOnly ? (
                    <span className={`pw2-flagpill ${isPrevailingWage ? 'on' : 'off'}`}>
                      <span className="dot" />
                      {isPrevailingWage ? 'PW Job' : 'Not PW'}
                    </span>
                  ) : (
                    <div className="pw2-flag">
                      <div className="pf-txt">
                        <span className="pf-lab">Prevailing wage job</span>
                        <span className="pf-sub">Affects certified payroll reporting</span>
                      </div>
                      <label className="pw2-switch">
                        <input
                          type="checkbox"
                          checked={isPrevailingWage}
                          onChange={e => requestPwToggle(e.target.checked)}
                        />
                        <span className="track" />
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Rate schedule toolbar ── */}
              <div className="pw2-rate-toolbar">
                <div>
                  <div className="rt-title">Rate schedule</div>
                  <div className="rt-sub">Grouped by classification · rows sorted newest-first</div>
                </div>
                <div className="grow" />
                {!isReadOnly && (
                  <button
                    type="button"
                    className="pw2-btn primary"
                    onClick={() => openAddRate()}
                  >
                    <Plus /> Add rate
                  </button>
                )}
              </div>

              {/* ── Empty state ── */}
              {groupsWithRates.length === 0 && (
                <div className="pw2-card">
                  <div className="pw2-empty">
                    <div className="eic"><Briefcase /></div>
                    <p className="et">No rates configured</p>
                    <p>
                      {isReadOnly
                        ? 'Rate schedules are managed on the production environment.'
                        : "Add rate records to build out this project's prevailing wage schedule."}
                    </p>
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="pw2-btn primary"
                        onClick={() => openAddRate()}
                      >
                        <Plus /> Add rate
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Classification groups ── */}
              {groupsWithRates.map(g => {
                const sortedRates = g.rates
                  .slice()
                  .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));

                return (
                  <div key={g.pwClassificationID} className="pw2-rate-group">
                    {/* Group header */}
                    <div className="pw2-rg-head">
                      <div className="rg-ic"><DollarSign /></div>
                      <span className="rg-name">{g.classificationName}</span>
                      {g.altPWClassificationID != null && (
                        <span className="rg-alt">{g.altPWClassificationID}</span>
                      )}
                      <div className="rg-meta">
                        {!isReadOnly && (
                          <button
                            type="button"
                            className="pw2-btn sm"
                            onClick={() => openAddRate(g.pwClassificationID)}
                          >
                            <Plus /> Add rate
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Column headers */}
                    <div className={`pw2-rate-cols${isReadOnly ? ' ro' : ''}`}>
                      <span />
                      <span>Effective date</span>
                      <span className="num">Base rate</span>
                      <span className="num col-tf">Total fringe</span>
                      <span className="num">Total pkg</span>
                      <span className="col-notes">Notes</span>
                      {!isReadOnly && <span className="col-edit" />}
                    </div>

                    {/* Rate rows */}
                    {sortedRates.map(rate => {
                      const status = getRateStatus(rate, TODAY, sortedRates);
                      const isOpen = openRows.has(rate.pwClassificationRateID);
                      const pkg = rate.baseHourlyRate + rate.totalFringeRate;

                      return (
                        <React.Fragment key={rate.pwClassificationRateID}>
                          <div
                            className={[
                              'pw2-rate-row',
                              isReadOnly ? 'ro' : '',
                              isOpen ? 'open' : '',
                              status,
                            ].filter(Boolean).join(' ')}
                            onClick={() => toggleRow(rate.pwClassificationRateID)}
                          >
                            <ChevronRight className="rr-caret" />

                            <div className="rr-date">
                              {fmtDate(rate.effectiveDate)}
                              {status === 'current' && (
                                <span className="pw2-cur-badge">
                                  <span className="dot" />
                                  Current
                                </span>
                              )}
                              {status === 'scheduled' && (
                                <span className="pw2-sched-badge">Scheduled</span>
                              )}
                            </div>

                            <div className="rr-num">${rate.baseHourlyRate.toFixed(2)}</div>
                            <div className="rr-num rr-fringe rr-tf">${rate.totalFringeRate.toFixed(2)}</div>
                            <div className="rr-num total">${pkg.toFixed(2)}</div>
                            <div className="rr-notes">{rate.notes ?? '—'}</div>

                            {!isReadOnly && (
                              <div className="rr-edit">
                                <button
                                  type="button"
                                  className="pw2-btn sm ghost"
                                  onClick={e => {
                                    e.stopPropagation();
                                    openEditRate(rate);
                                  }}
                                >
                                  <Edit /> Edit
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Fringe breakdown (CSS adjacent-sibling .open + .pw2-rr-detail) */}
                          <div className="pw2-rr-detail">
                            <div className="pw2-fringe-break">
                              <div className="fb-h">
                                <DollarSign /> Fringe breakdown
                              </div>
                              {rate.fringeItems.length === 0 ? (
                                <div className="pw2-fb-empty">
                                  No fringe items on this rate record.
                                </div>
                              ) : (
                                <>
                                  {rate.fringeItems.map(fi => (
                                    <div key={fi.pwFringeTypeID} className="pw2-fb-item">
                                      <span className="fb-name">{fi.fringeTypeName}</span>
                                      <span className="fb-amt">${fi.hourlyAmount.toFixed(2)}/hr</span>
                                    </div>
                                  ))}
                                  <div className="pw2-fb-total">
                                    <span className="fb-name">Total fringe</span>
                                    <span className="fb-amt">${rate.totalFringeRate.toFixed(2)}/hr</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>{/* pw2-page */}

        {/* ── Toasts ── */}
        <div className="pw2-toast-wrap">
          {toasts.map(t => (
            <div key={t.id} className={`pw2-toast ${t.type}`}>
              {t.type === 'ok' ? <CheckCircle /> : <AlertTriangle />}
              <span className="t-msg">{t.message}</span>
            </div>
          ))}
        </div>

        {/* ── PW flag confirm modal ── */}
        {confirmPwToggle && (
          <div
            className="pw2-modal-bg"
            onClick={() => { if (!pwToggling) setConfirmPwToggle(null); }}
          >
            <div className="pw2-modal" onClick={e => e.stopPropagation()}>
              <div className="pw2-modal-head">
                <div className="mi"><Briefcase /></div>
                <div>
                  <h3>
                    {confirmPwToggle.target
                      ? 'Mark as prevailing wage job?'
                      : 'Remove prevailing wage designation?'}
                  </h3>
                  <p className="ms">This affects certified payroll reporting.</p>
                </div>
                <button
                  type="button"
                  className="x"
                  onClick={() => { if (!pwToggling) setConfirmPwToggle(null); }}
                >
                  <X />
                </button>
              </div>
              <div className="pw2-modal-body">
                <div className="pw2-confirm-summary">
                  <b>{selectedProject?.name}</b> ({selectedProject?.altProjectID})<br />
                  {confirmPwToggle.target
                    ? 'This project will be flagged as a prevailing wage job. Applicable rates and reports will reflect this designation.'
                    : 'This project will no longer be flagged as a prevailing wage job. Existing rate records are not deleted.'}
                </div>
              </div>
              <div className="pw2-modal-foot">
                <button
                  type="button"
                  className="pw2-btn ghost"
                  disabled={pwToggling}
                  onClick={() => setConfirmPwToggle(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pw2-btn primary"
                  disabled={pwToggling}
                  onClick={confirmTogglePw}
                >
                  {pwToggling
                    ? 'Saving…'
                    : confirmPwToggle.target
                      ? 'Mark as PW job'
                      : 'Remove designation'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Rate modal ── */}
        {rateModalOpen && (
          <div className="pw2-modal-bg" onClick={closeRateModal}>
            <div className="pw2-modal wide" onClick={e => e.stopPropagation()}>
              <div className="pw2-modal-head">
                <div className="mi"><DollarSign /></div>
                <div>
                  <h3>{editingRate ? 'Edit rate record' : 'Add rate record'}</h3>
                  <p className="ms">{selectedProject?.name}</p>
                </div>
                <button type="button" className="x" onClick={closeRateModal}><X /></button>
              </div>

              <form onSubmit={submitRate}>
                <div className="pw2-modal-body">

                  {/* Classification */}
                  <div className="pw2-frow">
                    <label>
                      PW Classification
                      {!editingRate && <span className="req"> *</span>}
                    </label>
                    {editingRate ? (
                      <div className="pw2-locked-field">
                        <span className="lf-nm">
                          {getGroupForClassification(rateClassificationId)?.classificationName}
                        </span>
                        {getGroupForClassification(rateClassificationId)?.altPWClassificationID != null && (
                          <span className="lf-alt">
                            {getGroupForClassification(rateClassificationId)?.altPWClassificationID}
                          </span>
                        )}
                        <span className="lf-lock"><Lock /></span>
                      </div>
                    ) : (
                      <>
                        <select
                          className={`pw2-select${valErrs.classification ? ' err' : ''}`}
                          value={rateClassificationId ?? ''}
                          onChange={e => {
                            setRateClassificationId(parseInt(e.target.value, 10) || null);
                            setValErrs(prev => { const n = { ...prev }; delete n.classification; return n; });
                          }}
                        >
                          <option value="">Select classification</option>
                          {groups.map(g => (
                            <option key={g.pwClassificationID} value={g.pwClassificationID}>
                              {g.classificationName}
                              {g.altPWClassificationID != null ? ` (${g.altPWClassificationID})` : ''}
                            </option>
                          ))}
                        </select>
                        {valErrs.classification && (
                          <div className="pw2-inline-err">
                            <AlertTriangle />{valErrs.classification}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Effective date + Base rate */}
                  <div className="pw2-frow2">
                    <div className="pw2-frow" style={{ marginBottom: 0 }}>
                      <label>Effective date <span className="req">*</span></label>
                      <input
                        type="date"
                        className={`pw2-input${valErrs.date ? ' err' : ''}`}
                        value={effectiveDate}
                        onChange={e => {
                          setEffectiveDate(e.target.value);
                          setValErrs(prev => { const n = { ...prev }; delete n.date; return n; });
                        }}
                      />
                      {valErrs.date && (
                        <div className="pw2-inline-err">
                          <AlertTriangle />{valErrs.date}
                        </div>
                      )}
                    </div>

                    <div className="pw2-frow" style={{ marginBottom: 0 }}>
                      <label>Base hourly rate <span className="req">*</span></label>
                      <div className="pw2-amt-wrap">
                        <span className="cur">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="0.00"
                          className={`pw2-input${valErrs.base ? ' err' : ''}`}
                          value={baseHourlyRate}
                          onChange={e => {
                            setBaseHourlyRate(e.target.value);
                            setValErrs(prev => { const n = { ...prev }; delete n.base; return n; });
                          }}
                        />
                      </div>
                      {valErrs.base && (
                        <div className="pw2-inline-err">
                          <AlertTriangle />{valErrs.base}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="pw2-frow">
                    <label>
                      Notes <span className="opt">(optional)</span>
                    </label>
                    <input
                      className="pw2-input"
                      placeholder="e.g. Effective per county agreement"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                    />
                  </div>

                  <div className="pw2-divider" />

                  {/* Fringe rows header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span className="pw2-subhead" style={{ marginBottom: 0 }}>Fringe line items</span>
                    <button type="button" className="pw2-btn sm" onClick={addFringeRow}>
                      <Plus /> Add row
                    </button>
                  </div>

                  {/* Fringe rows */}
                  <div className="pw2-fringe-rows">
                    {fringeRows.map((row, idx) => {
                      const isDup = !!valErrs[`fringe_dup_${idx}`];
                      const noType = !!valErrs[`fringe_type_${idx}`];
                      const noAmt = !!valErrs[`fringe_amt_${idx}`];
                      return (
                        <div key={idx}>
                          <div className="pw2-fringe-input-row">
                            <select
                              className={`pw2-select${isDup || noType ? ' err' : ''}`}
                              value={row.pwFringeTypeID || ''}
                              onChange={e =>
                                updateFringeRow(idx, {
                                  pwFringeTypeID: parseInt(e.target.value, 10) || 0,
                                })
                              }
                            >
                              <option value="">Select fringe type</option>
                              {fringeTypeOptions.map(ft => (
                                <option key={ft.pwFringeTypeID} value={ft.pwFringeTypeID}>
                                  {ft.name}
                                </option>
                              ))}
                            </select>
                            <div className="pw2-amt-wrap">
                              <span className="cur">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                placeholder="0.00"
                                className={`pw2-input${noAmt ? ' err' : ''}`}
                                value={row.hourlyAmount}
                                onChange={e =>
                                  updateFringeRow(idx, { hourlyAmount: e.target.value })
                                }
                              />
                            </div>
                            <button
                              type="button"
                              className="rm-row"
                              onClick={() => removeFringeRow(idx)}
                            >
                              <X />
                            </button>
                          </div>
                          {(isDup || noType) && (
                            <div className="pw2-inline-err" style={{ marginTop: 4 }}>
                              <AlertTriangle />
                              {isDup ? 'Duplicate fringe type.' : 'Select a fringe type.'}
                            </div>
                          )}
                          {noAmt && (
                            <div className="pw2-inline-err" style={{ marginTop: 4 }}>
                              <AlertTriangle />Must be a positive number.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Live footer */}
                  <div className="pw2-fringe-foot">
                    <span className="ff-lab">Total fringe</span>
                    <span className="ff-val">${liveFringeTotal.toFixed(2)}/hr</span>
                    <div className="ff-div" />
                    <span className="ff-lab total">Total package</span>
                    <span className="ff-val total">${livePkgTotal.toFixed(2)}/hr</span>
                  </div>
                </div>{/* pw2-modal-body */}

                <div className="pw2-modal-foot">
                  <button type="button" className="pw2-btn ghost" onClick={closeRateModal}>
                    Cancel
                  </button>
                  {!isReadOnly && (
                    <button type="submit" className="pw2-btn primary" disabled={submitting}>
                      {submitting ? 'Saving…' : editingRate ? 'Save changes' : 'Add rate'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>{/* pwv2 */}
    </PageWrapper>
  );
};

export default PWProjectSetupPage;
